import { Controller } from "@hotwired/stimulus";
import offlineProgressSync from "../../../../src/react/helpers/OfflineProgressSync";
import {
  hasInternetAccess,
  seemsOffline,
  visit,
} from "../../../../utils/connectivity";
import { isPwaEnabled } from "../../../../utils/offline_gating";
import {
  quizQuestionUrl,
  cacheQuizUrl,
  isQuizUrlCached,
  resumeUrlKey,
  stripWelcomeApplyModeFromUrl,
} from "../../../../utils/quiz_offline_cache";

const answerFrom = (payload) => ({
  answerIndex: String(payload["quiz_item_response[answer_index]"]),
  struckIndexes: payload["indexes[]"] ?? [],
  confidence: payload["quiz_item_response[confidence]"],
});

/**
 * Handles offline submission + syncing for the quiz player.
 * When offline, form submissions are queued in IndexedDB and
 * replayed once connectivity is restored.
 */
export default class extends Controller {
  static targets = ["form", "revealTemplate"];

  static outlets = ["offline-navigation"];

  static values = {
    quizId: Number,
    questionIndex: Number,
    nextIndex: Number,
    totalQuestions: Number,
    completedQuestions: Number,
    mode: String,
    quizCompleted: Boolean,
    lastQuestion: Boolean,
    correctAnswer: Number,
  };

  connect() {
    stripWelcomeApplyModeFromUrl();
    this.startTime = Date.now();
    this.pwaEnabled = isPwaEnabled();
    this.navigationLocked = false;
    this.boundHandleNavClick = this.handleNavClick.bind(this);
    document.addEventListener("click", this.boundHandleNavClick, true);

    if (this.pwaEnabled) {
      this.rehydrateFromQueue();
      this.rememberResumeUrl();
      this.restoreDraft();
      this.cacheCurrentPage();
      this.prefetchNextQuestion();
      if (this.isStudyMode()) {
        this.prefetchAllQuestions();
      }
    }
  }

  rememberResumeUrl() {
    const key = resumeUrlKey(document.body.dataset.userId);
    if (this.quizCompletedValue) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(
        key,
        window.location.pathname + window.location.search,
      );
    }
  }

  async restoreDraft() {
    if (!this.hasFormTarget) return;

    const draft = await offlineProgressSync.readQuizItemResponseDraft(
      this.quizIdValue,
      this.questionIndexValue,
    );
    if (!draft) return;

    const { struckIndexes } = answerFrom(draft);
    this.formTarget.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.checked = draft[radio.name] === radio.value;
    });
    this.formTarget
      .querySelectorAll('input[name="indexes[]"]')
      .forEach((checkbox) => {
        const struck = struckIndexes.includes(checkbox.value);
        checkbox.checked = struck;
        const choice = this.formTarget.querySelector(
          `[data-quiz-player--quiz-mcq-target="choice"][data-index="${checkbox.value}"]`,
        );
        choice?.classList.toggle("line-through", struck);
        const button = this.formTarget.querySelector(
          `[data-quiz-player--quiz-mcq-target="strikeButton"][data-index="${checkbox.value}"]`,
        );
        button?.setAttribute("aria-pressed", struck ? "true" : "false");
      });
    // ponytail: direct call avoids bubbling `change`, which would re-save the draft just restored.
    this.application
      ?.getControllerForElementAndIdentifier(
        this.formTarget,
        "quiz-player--quiz-mcq",
      )
      ?.updateSubmitState();
  }

  async handleSubmit(event) {
    // Always stamp response time so online submits still work
    this.setResponseTime();

    if (!this.pwaEnabled) return;
    const offlineSync = offlineProgressSync;
    if (!offlineSync) return;

    let shouldHandleOffline = offlineSync.shouldQueue() || seemsOffline();
    if (!shouldHandleOffline) {
      event.preventDefault();
      const online = await hasInternetAccess(900);
      shouldHandleOffline = !online;
      if (!online && typeof offlineSync.isOnline === "boolean") {
        offlineSync.isOnline = false;
      }
      if (!shouldHandleOffline) {
        await Promise.all([
          offlineSync.clearQuizItemResponseDraft(
            this.quizIdValue,
            this.questionIndexValue,
          ),
          offlineSync.removeQueuedQuizItemResponse(
            this.quizIdValue,
            this.questionIndexValue,
          ),
        ]);
        if (this.lastQuestionValue && this.isQuizMode())
          localStorage.removeItem(resumeUrlKey(document.body.dataset.userId));
        if (this.hasFormTarget) this.formTarget.submit();
        return;
      }
    }

    // Offline: store the submission locally and move on
    event.preventDefault();
    // Stop any other listeners from submitting/reloading the form.
    event.stopPropagation();
    event.stopImmediatePropagation?.();
    const payload = this.serializeForm();
    const correct = this.isStudyMode() ? this.isCorrect(payload) : undefined;

    try {
      await offlineSync.queueQuizItemResponse({
        quizId: this.quizIdValue,
        questionIndex: this.questionIndexValue,
        mode: this.modeValue,
        formData: payload,
        correct,
      });
    } catch (error) {
      console.error("[QuizOffline] Failed to queue response", error);
      this.showFlash("Unable to save offline. Please try again when online.");
      return false;
    }
    this.disableSubmitControls();
    await offlineSync
      .clearQuizItemResponseDraft(this.quizIdValue, this.questionIndexValue)
      .catch((error) =>
        console.error("[QuizOffline] Failed to clear draft", error),
      );

    const offlineFinalQuestionMessage =
      "Final answer saved locally. Reconnect to finish this quiz and view results.";
    const isOffline = seemsOffline();

    if (this.lastQuestionValue && isOffline) {
      this.showOfflineCompletionMessage(offlineFinalQuestionMessage);
    }
    this.bumpLocalProgress();

    if (this.isActiveQuizMode()) {
      if (!this.lastQuestionValue) {
        await this.navigateAfterQueuedSubmit();
      }
    } else if (this.isStudyMode()) {
      this.revealFromPayload(payload);
      if (isOffline && (await this.offlineQuizCompleted())) {
        if (!this.showEndQuizModal()) {
          this.showOfflineCompletionMessage(offlineFinalQuestionMessage);
        }
      }
    }
    return false;
  }

  async offlineQuizCompleted() {
    const total = this.totalQuestionsValue;
    if (!total) return false;

    const answered = new Set([this.questionIndexValue]);
    this.sidebarRows().forEach((row, index) => {
      if (this.hasServerStatusIcon(row)) answered.add(index + 1);
    });

    try {
      const queued = await offlineProgressSync.pendingFor(this.quizIdValue);
      queued.forEach((update) => {
        const index = Number(update.questionIndex);
        if (index >= 1) answered.add(index);
      });
    } catch (_error) {
      // Sidebar + current question already give a usable estimate.
    }

    if (this.sidebarRows().length === 0) {
      return Math.max(answered.size, this.completedQuestionsValue + 1) >= total;
    }

    return answered.size >= total;
  }

  showEndQuizModal() {
    const modal = document.getElementById("quiz-player-offline-complete");
    if (!modal) return false;

    const controller = this.application?.getControllerForElementAndIdentifier(
      modal,
      "railsui-modal",
    );
    if (!controller) return false;

    controller.show();
    return true;
  }

  saveDraft() {
    if (!this.pwaEnabled) return;
    offlineProgressSync.saveQuizItemResponseDraft(
      this.quizIdValue,
      this.questionIndexValue,
      this.serializeForm(),
    );
  }

  setResponseTime() {
    if (!this.hasFormTarget) return;
    const input = this.formTarget.querySelector("#responseTime");
    if (input) {
      input.value = Date.now() - (this.startTime || Date.now());
    }
  }

  serializeForm() {
    const payload = {};
    if (!this.hasFormTarget) return payload;

    const formData = new FormData(this.formTarget);
    formData.forEach((value, key) => {
      // Preserve array params like indexes[]
      if (key.endsWith("[]")) {
        if (!payload[key]) payload[key] = [];
        payload[key].push(value);
      } else if (payload[key]) {
        payload[key] = Array.isArray(payload[key])
          ? [...payload[key], value]
          : [payload[key], value];
      } else {
        payload[key] = value;
      }
    });

    return payload;
  }

  buildQuestionUrl(index) {
    return quizQuestionUrl(this.quizIdValue, index, { mode: this.modeValue });
  }

  prefetchNextQuestion() {
    if (this.lastQuestionValue) return;
    if (!navigator.onLine) return;

    this.cacheQuestion(this.nextIndexValue);
  }

  async prefetchAllQuestions() {
    if (this.prefetchedAllQuestions) return;
    if (!navigator.onLine) return;
    if (!this.totalQuestionsValue || !this.quizIdValue) return;

    const urls = [];
    for (let i = 1; i <= this.totalQuestionsValue; i++) {
      if (i === this.questionIndexValue) continue; // current page already loaded
      urls.push(i);
    }

    await Promise.all(urls.map((idx) => this.cacheQuestion(idx)));

    this.prefetchedAllQuestions = true;
  }

  cacheCurrentPage() {
    if (!navigator.onLine) return;
    this.cacheQuestion(this.questionIndexValue);
  }

  cacheQuestion(index) {
    return this.cacheUrl(this.buildQuestionUrl(index));
  }

  // The fetch alone populates the offline cache: the controlling service worker
  // caches successful GET responses under its own versioned cache name.
  async cacheUrl(url) {
    if (seemsOffline()) return;
    await cacheQuizUrl(url);
  }

  async isUrlCached(url) {
    return isQuizUrlCached(url);
  }

  async navigateWithCacheGuard(url, { resumeAfterClose = false } = {}) {
    if (!seemsOffline() || !this.pwaEnabled) {
      visit(url);
      return true;
    }

    return this.offlineNavigationOutlet.routeOffline(url, {
      cached: await this.isUrlCached(url),
      resumeAfterClose,
    });
  }

  handleNavClick(event) {
    if (!this.pwaEnabled) return;
    const clickable = event.target.closest("a, button");
    if (!clickable) return;

    if (seemsOffline() && this.isOfflineBlockedAction(clickable)) {
      event.preventDefault();
      event.stopPropagation();
      this.offlineNavigationOutlet.showFeatureUnavailable(clickable);
      return;
    }

    const contentPanelForm = clickable.closest("#quiz-player-questions form");
    if (contentPanelForm && seemsOffline()) {
      event.preventDefault();
      event.stopPropagation();
      this.navigateWithCacheGuard(this.contentPanelNavUrl(contentPanelForm));
      return;
    }

    const anchor = clickable.tagName === "A" ? clickable : null;
    if (!anchor) return;

    const isNavLink =
      anchor.classList.contains("next-question") ||
      anchor.classList.contains("prev-question");
    const isSidebarQuestionLink =
      (anchor.classList.contains("quiz-sidebar-question-name") &&
        Boolean(anchor.closest(".quiz-sidebar-list"))) ||
      Boolean(anchor.closest("#quiz-player-questions"));
    if (!isNavLink && !isSidebarQuestionLink) return;

    const href = anchor.getAttribute("href");
    if (!href) return;

    if (seemsOffline()) {
      event.preventDefault();
      this.navigateWithCacheGuard(href);
    }
  }

  disconnect() {
    document.removeEventListener("click", this.boundHandleNavClick, true);
  }

  isOfflineBlockedAction(element) {
    const action = element.getAttribute("data-action") || "";
    const isTodoTrigger = element.matches(
      ".add-todo-button, .todo-manage-button, .delete-auto-todo-link",
    );
    const isFlagToggle = element.matches(".response-flagged");
    const isViewRelatedCase = element.matches(".quiz-player-view-related-case");
    const needsNetwork = element.matches(
      ".report-issue, .support_btn a, .player-summary-button, .player-support-link, .player-report-issue",
    );
    const opensReport =
      action.includes("quiz-player--quiz-chrome#openReport") ||
      element.getAttribute("aria-controls") ===
        "quiz-player-report-issue-dialog";
    const isSaveExitTrigger = element.id === "save-question-to-todo";
    const opensResetModal =
      action.includes("quiz-player-aux#openResetModal") ||
      action.includes("quiz-player--quiz-chrome#openReset");
    const opensTodoModal =
      action.includes("to-do-modal#openTodoModal") ||
      action.includes("saq#openTodoModal") ||
      action.includes("content-library-modal#openTodoModal") ||
      action.includes("text-decoration#openTodoModal") ||
      action.includes("aquifer-ui-quiz-results-todo-modal#openTodoModal");
    const opensSaveQuizTodoModal =
      action.includes("content-library-modal#openSaveQuizTodoModal") ||
      action.includes("text-decoration#openSaveQuizTodoModal") ||
      action.includes("quiz-player--quiz-chrome#openSaveTodo");

    return (
      isTodoTrigger ||
      isFlagToggle ||
      isViewRelatedCase ||
      needsNetwork ||
      opensReport ||
      isSaveExitTrigger ||
      opensResetModal ||
      opensTodoModal ||
      opensSaveQuizTodoModal
    );
  }

  showFlash(message) {
    const flash = document.getElementById("saq-player-flash-messages");
    if (flash) {
      flash.textContent = message;
      flash.classList.remove("hidden");
    }
  }

  showOfflineCompletionMessage(message) {
    const completionBanner = document.getElementById("offline-quiz-sync-notice");
    if (completionBanner) {
      completionBanner.textContent = message;
      completionBanner.classList.remove("hidden");
    }
  }

  isStudyMode() {
    return (this.modeValue || "").toLowerCase().includes("study");
  }

  isQuizMode() {
    return (this.modeValue || "").toLowerCase().includes("quiz");
  }

  isActiveQuizMode() {
    return this.isQuizMode() && !this.quizCompletedValue;
  }

  destinationUrlAfterSubmit() {
    if (!this.quizIdValue) return null;

    if (this.lastQuestionValue && this.isQuizMode()) {
      const params = new URLSearchParams();
      params.set("question_index", this.questionIndexValue);
      if (this.modeValue) params.set("mode", this.modeValue);
      return `/quizzes/${this.quizIdValue}/end_quiz?${params.toString()}`;
    }

    return this.buildQuestionUrl(this.nextIndexForMode());
  }

  async navigateAfterQueuedSubmit() {
    const destinationUrl = this.destinationUrlAfterSubmit();
    if (!destinationUrl || this.navigationLocked) return false;

    this.navigationLocked = true;
    return this.navigateWithCacheGuard(destinationUrl, { resumeAfterClose: true });
  }

  nextIndexForMode() {
    // Offline we should proceed to the next question even in study mode.
    if (this.isStudyMode())
      return this.nextIndexValue || this.questionIndexValue;
    return this.nextIndexValue;
  }

  async rehydrateFromQueue() {
    if (!this.quizIdValue) return;

    let queued;
    try {
      queued = await offlineProgressSync.pendingFor(this.quizIdValue);
    } catch (_error) {
      return;
    }
    this.revealQueuedResponse(queued);
    queued
      .filter((update) => typeof update.correct === "boolean")
      .forEach((update) =>
        this.updateSidebarStatus(Number(update.questionIndex), update.correct),
      );
    this.reconcileProgressFromQueue(queued);
  }

  revealQueuedResponse(queued) {
    if (!this.hasRevealTemplateTarget) return;

    const response = queued.findLast(
      (update) => Number(update.questionIndex) === this.questionIndexValue,
    );
    if (response) this.revealFromPayload(response.formData);
  }

  isCorrect(payload) {
    return Number(answerFrom(payload).answerIndex) === this.correctAnswerValue;
  }

  revealFromPayload(payload) {
    if (!this.hasRevealTemplateTarget) return;

    const reveal = this.revealTemplateTarget.content.cloneNode(true);
    const { answerIndex, struckIndexes, confidence } = answerFrom(payload);
    const correct = this.isCorrect(payload);

    reveal.querySelectorAll("[data-choice-index]").forEach((row) => {
      const index = String(row.dataset.choiceIndex);
      const chosen = index === answerIndex;
      const radio = row.querySelector('input[type="radio"]');
      if (radio) radio.checked = chosen;
      row
        .querySelector(".player-option-label")
        ?.classList.toggle("line-through", struckIndexes.includes(index));
      row.querySelectorAll("[data-reveal-certainty]").forEach((pill) => {
        if (!chosen || pill.dataset.revealCertainty !== confidence)
          pill.remove();
      });
      row.querySelectorAll("[data-reveal-choice-correct]").forEach((icon) => {
        const marksCorrectChoice = icon.dataset.revealChoiceCorrect === "true";
        if (marksCorrectChoice && index !== String(this.correctAnswerValue)) {
          icon.remove();
          return;
        }
        if (!marksCorrectChoice && !(chosen && !correct)) {
          icon.remove();
        }
      });
    });

    reveal.querySelectorAll("[data-reveal-correct]").forEach((icon) => {
      if (icon.dataset.revealCorrect !== String(correct)) icon.remove();
    });

    const root =
      this.element.querySelector(
        "[data-quiz-player--quiz-offline-target='questionRoot']",
      ) ||
      this.element.querySelector(".player-question-body") ||
      this.element;
    root.replaceChildren(reveal);
    this.updateSidebarStatus(this.questionIndexValue, correct);
  }

  sidebarRows() {
    const dsRows = document.querySelectorAll(
      "#quiz-player-questions.aq-content-panel > ol > li",
    );
    if (dsRows.length > 0) return Array.from(dsRows);

    return Array.from(
      document.querySelectorAll(".quiz-sidebar-list .quiz-sidebar-question"),
    );
  }

  contentPanelNavUrl(form) {
    const action = form.getAttribute("action") || "";
    const formParams = new URLSearchParams(new FormData(form));
    let url;
    try {
      url = new URL(action, window.location.origin);
    } catch {
      return action;
    }

    formParams.forEach((value, key) => {
      url.searchParams.set(key, value);
    });

    return `${url.pathname}${url.search}`;
  }

  updateSidebarStatus(questionIndex, isCorrect) {
    const li = this.sidebarRows()[questionIndex - 1];
    if (!li) return;

    const row =
      li.querySelector(".quiz-player-nav-item") ||
      li.querySelector(".aq-content-panel__row, .content-panel-item") ||
      li;
    if (this.hasServerStatusIcon(row) || this.hasServerStatusIcon(li)) return;

    const status = isCorrect ? "correct" : "incorrect";
    row.dataset.state = status;

    const labeled =
      row.querySelector(".content-panel-item[aria-label], [aria-label]") || row;
    const baseLabel = this.offlineSidebarBaseAriaLabel(
      labeled.getAttribute("aria-label"),
    );
    labeled.setAttribute("aria-label", `${baseLabel}, ${status}`);

    let slot = row.querySelector(
      ".aq-content-panel__icon, .offline-status-slot, .quiz-player-nav-item__trailing",
    );
    if (!slot) {
      slot = document.createElement("span");
      slot.className = "offline-status-slot aq-content-panel__icon";
      row.appendChild(slot);
    } else if (slot.classList.contains("quiz-player-nav-item__trailing")) {
      let offlineSlot = slot.querySelector(".offline-status-slot");
      if (!offlineSlot) {
        offlineSlot = document.createElement("span");
        offlineSlot.className = "offline-status-slot";
        slot.prepend(offlineSlot);
      }
      slot = offlineSlot;
    }

    slot.innerHTML = "";
    const mark = document.createElement("span");
    mark.className = `offline-sidebar-status ${isCorrect ? "response-correct" : "response-incorrect"}`;
    mark.textContent = isCorrect ? "✓" : "✕";
    mark.setAttribute("aria-hidden", "true");
    slot.appendChild(mark);
  }

  offlineSidebarBaseAriaLabel(label) {
    if (!label) return "Question";

    return label.replace(/, (correct|incorrect)$/i, "").trim() || "Question";
  }

  disableSubmitControls() {
    if (!this.hasFormTarget) return;
    this.formTarget
      .querySelectorAll('button[type="submit"], input[type="submit"]')
      .forEach((btn) => {
        btn.setAttribute("disabled", "disabled");
      });
  }

  bumpLocalProgress() {
    // Only adjust client-side when offline to avoid double counting in online mode.
    if (!seemsOffline()) return;

    this.offlineAnswered = (this.offlineAnswered ?? 0) + 1;
    this.paintProgress(this.completedQuestionsValue + this.offlineAnswered);
  }

  reconcileProgressFromQueue(queued) {
    const serverAnswered = new Set(
      this.sidebarRows()
        .map((row, position) =>
          this.hasServerStatusIcon(row) ? position + 1 : null,
        )
        .filter(Boolean),
    );
    const queuedOnly = new Set(
      queued
        .map((update) => Number(update.questionIndex))
        .filter((index) => !serverAnswered.has(index)),
    );
    if (queuedOnly.size === 0) return;

    this.offlineAnswered = queuedOnly.size;
    this.paintProgress(this.completedQuestionsValue + this.offlineAnswered);
  }

  hasServerStatusIcon(row) {
    if (
      row.querySelector(
        ".quiz-player-nav-status-icon--correct, .quiz-player-nav-status-icon--incorrect",
      )
    ) {
      return true;
    }

    const state =
      row.dataset?.state || row.querySelector("[data-state]")?.dataset?.state;
    if (state === "correct" || state === "incorrect" || state === "visited") {
      return !row.querySelector(".offline-sidebar-status");
    }

    return Boolean(
      row.querySelector(
        "img.response-correct:not(.offline-sidebar-status), img.response-incorrect:not(.offline-sidebar-status)",
      ),
    );
  }

  paintProgress(completed) {
    const total = this.totalQuestionsValue || 0;
    const percent = total > 0 ? Math.min((completed / total) * 100, 100) : 0;

    document.querySelectorAll(".railsui-progress__fill").forEach((fill) => {
      fill.style.width = `${percent}%`;
    });
    document.querySelectorAll('[role="progressbar"]').forEach((bar) => {
      bar.setAttribute("aria-valuenow", String(completed));
      bar.setAttribute("aria-valuemax", String(total));
    });

    const legacyFill = document.querySelector(".progress-value");
    if (legacyFill) {
      legacyFill.style.width = `${percent}%`;
      legacyFill.setAttribute("aria-valuenow", completed);
      legacyFill.setAttribute("aria-valuemax", total);
      if (percent >= 100) legacyFill.classList.add("complete");
    }

    const live = document.getElementById("progressbar-live");
    if (live) {
      live.textContent = `You have answered ${completed} of ${total} questions.`;
    }
  }
}
